import { useCallback, useEffect, useMemo, useState } from 'react';

// Breakpoint values from @volvo/vcdk-tailwind (breakpoint.css)
const BREAKPOINTS = {
  sm: '(min-width: 600px)',
  md: '(min-width: 905px)',
  lg: '(min-width: 1240px)',
  xl: '(min-width: 1440px)',
} as const;

type TBreakpointNodes<T = void> = {
  default: T;
  sm?: T;
  md?: T;
  lg?: T;
  xl?: T;
};

export type TMediaQueryNodesFunc = <T>(opt: TBreakpointNodes<T>) => T;

const breakPointsOrder = ['xl', 'lg', 'md', 'sm', 'default'] as const;

const useMediaQuery = () => {
  // Memoize media query objects to prevent recreation on every render
  const mediaQueries = useMemo(
    () => ({
      sm: window.matchMedia(BREAKPOINTS.sm),
      md: window.matchMedia(BREAKPOINTS.md),
      lg: window.matchMedia(BREAKPOINTS.lg),
      xl: window.matchMedia(BREAKPOINTS.xl),
    }),
    []
  );

  const initMQ = useCallback(() => {
    switch (true) {
      case mediaQueries.xl.matches:
        return 'xl';
      case mediaQueries.lg.matches:
        return 'lg';
      case mediaQueries.md.matches:
        return 'md';
      case mediaQueries.sm.matches:
        return 'sm';
      default:
        return 'default';
    }
  }, [mediaQueries]);

  const [activeWidth, setActiveWidth] = useState<keyof TBreakpointNodes>(initMQ);

  const mediaQueryNodes: TMediaQueryNodesFunc = useCallback(
    <T = void>(opt: TBreakpointNodes<T>) => {
      let node = opt[activeWidth];

      if (node !== undefined) {
        return node;
      }

      const indexOfActive = breakPointsOrder.findIndex((size) => size === activeWidth);

      for (let i = indexOfActive + 1; i < breakPointsOrder.length; i++) {
        if (node !== undefined) {
          break;
        }
        node = opt[breakPointsOrder[i]];
      }

      if (node === undefined) {
        node = opt.default;
      }

      return node;
    },
    [activeWidth]
  );

  useEffect(() => {
    const entries = Object.entries(mediaQueries) as [keyof typeof mediaQueries, MediaQueryList][];

    const handlers = entries.map(([, mq]) => {
      const handler = () => setActiveWidth(initMQ());
      mq.addEventListener('change', handler);
      return { mq, handler };
    });

    return () => {
      handlers.forEach(({ mq, handler }) => mq.removeEventListener('change', handler));
    };
  }, [mediaQueries, initMQ]);

  // Calculate reduceAnimations once and memoize
  const reduceAnimations = useMemo(() => window.matchMedia('(prefers-reduced-motion: reduce)').matches, []);

  return { mediaQueryNodes, activeWidth, reduceAnimations };
};

export default useMediaQuery;
